import { AsterixOutlined } from '@icon';
import classNames from 'classnames';
import React from 'react';
import { useTheme } from 'styled-components';
import Form from '../../form';
import { TFormVariant } from '../../form/Form.types';
import RadioGroup from './radio-group';
import { RadioSC } from './radio.styled';
import { useRadioGroup } from './radio.utils';
import hoistNonReactStatistics from 'hoist-non-react-statics';

interface RadioProps<T>
  extends Omit<
    React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    'ref' | 'children' | 'value'
  > {
  value: T;
  children?: React.ReactNode;
  variant?: TFormVariant;
  error?: boolean;
  required?: boolean;
}

const Radio = <T extends any>(props: RadioProps<T>, ref?: React.Ref<HTMLInputElement | null>) => {
  const {
    children,
    variant,
    className,
    style,
    value,
    onChange,
    error,
    required,
    checked,
    ...rest
  } = props;
  const theme = useTheme();
  const settings = Form.useFormSettings({ variant });
  const groupContext = useRadioGroup();
  const isChecked = groupContext ? groupContext.value === value : checked;
  const name = props.name ?? groupContext?.name;

  const innerRef = React.useRef<HTMLInputElement>(null);

  React.useImperativeHandle(ref, () => innerRef.current);

  const onRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e);
    groupContext?.onChange?.(e);
  };

  return (
    <RadioSC
      disabled={props.disabled}
      readOnly={props.readOnly}
      error={error}
      className={classNames('ui-radio', className, {
        'ui-radio-children': !!children,
      })}
      style={style}
    >
      <label>
        <span className="radio-control">
          <input
            type="radio"
            {...rest}
            ref={innerRef}
            value={value as any}
            onChange={onRadioChange}
            checked={isChecked}
            name={name}
          />
          <span className="radio-handle">
            <span className="ui-radio-mark" />
          </span>
        </span>
        {children || required ? (
          <span
            className="ui-radio-text-wrapper"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              innerRef.current?.click();
            }}
          >
            {required ? (
              <AsterixOutlined color={theme.color.solid.error} width={16} height={16} />
            ) : null}
            {children}
          </span>
        ) : null}
      </label>
    </RadioSC>
  );
};

Radio.Group = RadioGroup;

export default hoistNonReactStatistics(React.forwardRef(Radio), Radio) as typeof Radio;
